import React, { useState } from 'react';
import { Lock, Sparkles, Building2, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { TIER_CONFIG } from '../config/featureTiers';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UpgradeModal = ({ isOpen, onClose, requiredTier, featureName }) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const tierInfo = TIER_CONFIG[requiredTier] || TIER_CONFIG.pro;
  const isEnterprise = requiredTier === 'enterprise';

  const proFeatures = [
    'AI/GPU Cost Intelligence',
    'Zombie Resource Killer',
    'Cloud P&L Reporting',
    'AI Command Center',
    'Cost DNA Fingerprint',
    'Bill Shock Prevention',
  ];

  const enterpriseFeatures = [
    'Everything in Pro',
    'Commitment Intelligence',
    'Snowflake Intelligence Suite',
    'Kubernetes Cost Management',
    'Git-to-Bill Attribution',
    'SSO & SOC 2 Compliance',
  ];

  const features = isEnterprise ? enterpriseFeatures : proFeatures;

  const handleUpgrade = async (planId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      const response = await axios.post(`${API_URL}/payments/create-checkout`, {
        plan_id: planId,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="upgrade-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className={`px-6 pt-8 pb-6 ${
          isEnterprise 
            ? 'bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-600' 
            : 'bg-blue-500 bg-gradient-to-br from-blue-500 to-cyan-500'
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-xl">
              {isEnterprise ? <Building2 className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-white" />}
            </div>
            <div>
              <p className="text-white/80 text-xs font-medium uppercase tracking-wider">Upgrade to</p>
              <h2 className="text-2xl font-bold text-white">{tierInfo.label}</h2>
            </div>
          </div>
          {featureName && (
            <div className="flex items-center gap-2 mt-3 bg-white/10 rounded-lg px-3 py-2">
              <Lock className="w-4 h-4 text-white/70" />
              <p className="text-white/90 text-sm">
                <span className="font-semibold">{featureName}</span> requires {tierInfo.label}
              </p>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="px-6 py-5">
          <p className="text-sm font-medium text-slate-400 dark:text-slate-400 mb-3 uppercase tracking-wider">
            What you get
          </p>
          <ul className="space-y-2.5">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2.5">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  isEnterprise 
                    ? 'bg-cyan-100 dark:bg-cyan-900/30' 
                    : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <Check className={`w-3 h-3 ${
                    isEnterprise 
                      ? 'text-cyan-600 dark:text-cyan-400' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`} />
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6 space-y-3">
          {isEnterprise ? (
            <>
              <div className="text-center mb-2">
                <span className="text-3xl font-bold text-slate-900 dark:text-white">$199</span>
                <span className="text-slate-400 dark:text-slate-400">/month</span>
              </div>
              <Button 
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                onClick={() => handleUpgrade('enterprise')}
                disabled={loading}
                data-testid="upgrade-enterprise-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Processing...' : 'Upgrade to Enterprise'}
              </Button>
            </>
          ) : (
            <>
              <div className="text-center mb-2">
                <span className="text-3xl font-bold text-slate-900 dark:text-white">$49</span>
                <span className="text-slate-400 dark:text-slate-400">/month</span>
              </div>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
                onClick={() => handleUpgrade('pro')}
                disabled={loading}
                data-testid="upgrade-pro-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Processing...' : 'Upgrade to Pro'}
              </Button>
            </>
          )}
          <button 
            onClick={onClose}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 py-1"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
